import { useState } from "react";
import { mutate } from "swr";
import { clsx } from "clsx";
import { IconFileDescription } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { documentsService } from "../../../api_client";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import type { DocumentDto } from "../../../../api";
import { DocumentDtoStatusEnum, DocumentDtoTypeEnum } from "../../../../api";
import { docStatusChip, formatUploadDate } from "../../utils";
import { DatePicker } from "../../../../components/common/date-picker";
import { RejectForm } from "./reject-form";

const ONE_DAY_MS = 86_400_000;

function getDocTypeName(type: string) {
  if (type === DocumentDtoTypeEnum.Cr) return "Commercial Registration (CR)";
  if (type === DocumentDtoTypeEnum.TaxCard) return "Tax Card";
  return type;
}

// Ref is derived locally from type + upload date — not a server-assigned number.
function getDocRef(doc: DocumentDto): string {
  const d = new Date(doc.createdAt);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${doc.type}-${d.getFullYear()}-${mm}${dd}`;
}

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
    (err as { message?: string })?.message ||
    ""
  );
}

function defaultExpiryDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

const SWR_KEY_PREFIX = "documents-grouped-by-user";

interface DocumentCardProps {
  document: DocumentDto;
}

export function DocumentCard({ document }: DocumentCardProps) {
  const [mode, setMode] = useState<"idle" | "reject">("idle");
  const [expiresAt, setExpiresAt] = useState(defaultExpiryDate);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPending = document.status === DocumentDtoStatusEnum.Pending;

  const handleView = async () => {
    try {
      const { data } = await documentsService.getDocumentDownloadUrl(document.id);
      if (data.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      showErrorToast("Failed to get document URL");
    }
  };

  const handleApprove = async () => {
    if (!expiresAt) { showErrorToast("Please select an expiration date"); return; }
    setIsSubmitting(true);
    try {
      await documentsService.approveDocument(document.id, {
        expiresAt: new Date(`${expiresAt}T00:00:00.000Z`).toISOString(),
      });
      showSuccessToast("Document approved successfully");
      mutate((key) => typeof key === "string" && key.startsWith(SWR_KEY_PREFIX));
      setMode("idle");
    } catch (err) {
      showErrorToast(extractError(err) || "Failed to approve document");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedReason) return;
    const reason = notes.trim() ? `${selectedReason} — ${notes.trim()}` : selectedReason;
    setIsSubmitting(true);
    try {
      await documentsService.rejectDocument(document.id, { reason });
      showSuccessToast("Document rejected successfully");
      mutate((key) => typeof key === "string" && key.startsWith(SWR_KEY_PREFIX));
      setMode("idle");
      setSelectedReason(null);
      setNotes("");
    } catch (err) {
      showErrorToast(extractError(err) || "Failed to reject document");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileSizeStr = formatFileSize(document.fileSize);
  const uploadedStr = `Uploaded ${formatUploadDate(document.createdAt)}`;
  const fileMeta = [fileSizeStr, uploadedStr].filter(Boolean).join(" · ");

  const isRejectMode = mode === "reject";

  return (
    <div className={clsx(
      "flex flex-col rounded-2xl border bg-bgWhite shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
      isRejectMode ? "border-accentError" : "border-borderDefault",
    )}>
      <div className="flex items-start justify-between px-6 pt-5 pb-3 max-md:gap-3 max-md:px-4 max-md:[&>*:last-child]:shrink-0">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Heading variant="h3" tone="dark">{getDocTypeName(document.type)}</Heading>
          <Text variant="bodySm" tone="muted">Ref: {getDocRef(document)}</Text>
        </div>
        {!isRejectMode && docStatusChip(document.status)}
      </div>

      <div className="px-6 pb-4 max-md:px-4">
        <div className="flex items-center gap-3 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
          <IconFileDescription size={20} className="shrink-0 text-textMuted" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Text variant="bodyBold" tone="dark" className="max-md:break-all">{document.fileName}</Text>
            <Text variant="bodySm" tone="muted">{fileMeta}</Text>
          </div>
          <Button variant="ghost" size="sm" className="!px-0 shrink-0 !text-textDark" onClick={handleView}>
            View
          </Button>
        </div>
      </div>

      {isPending && (
        <div className="border-t border-borderLight px-6 py-4 flex flex-col gap-4 max-md:px-4">
          {mode === "idle" && (
            <div className="flex flex-col gap-3">
              <DatePicker
                label="Expiration date"
                value={expiresAt}
                min={new Date(Date.now() + ONE_DAY_MS).toISOString().slice(0, 10)}
                onChange={setExpiresAt}
              />
              <div className="flex gap-3">
                <Button variant="filledDark" size="md" onClick={handleApprove} disabled={isSubmitting || !expiresAt}>
                  {isSubmitting ? "Approving…" : "Approve"}
                </Button>
                <Button variant="secondary" size="md" onClick={() => setMode("reject")}>Reject</Button>
              </div>
            </div>
          )}

          {mode === "reject" && (
            <RejectForm
              selectedReason={selectedReason}
              onSelectReason={setSelectedReason}
              notes={notes}
              onNotesChange={setNotes}
              isSubmitting={isSubmitting}
              onReject={handleReject}
              onCancel={() => { setMode("idle"); setSelectedReason(null); setNotes(""); }}
            />
          )}
        </div>
      )}
    </div>
  );
}
